/**
 * La sesión en el cliente.
 *
 * Guarda **quién es** el usuario, no **qué puede hacer**: los permisos los
 * decide el Worker en cada petición. Lo de acá sirve para elegir qué pantallas
 * mostrar, y ocultar un botón nunca es seguridad.
 */

import {
  createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode,
} from 'react';
import type { AppRole } from '@cleanblue/shared';
import { api, ApiError } from './api';
import { CambiarContrasena } from '@/features/auth/CambiarContrasena';

const TEMP_PASSWORD_KEY = 'cb_temp_password';

/**
 * La temporal guardada al entrar, si había una — y la borra al leerla: es de
 * un solo uso, igual que la temporal misma.
 */
export function consumeTempPassword(): string | null {
  try {
    const value = sessionStorage.getItem(TEMP_PASSWORD_KEY);
    sessionStorage.removeItem(TEMP_PASSWORD_KEY);
    return value;
  } catch {
    return null;
  }
}

export interface SessionUser {
  profile_id: string;
  full_name: string;
  email: string | null;
  photo_key: string | null;
  role: AppRole;
  organization_id: string | null;
  must_change_password: boolean;
  unit_ids: string[];
  admin_unit_ids: string[];
}

interface SessionValue {
  user: SessionUser | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
}

const SessionContext = createContext<SessionValue | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<SessionUser | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { user: me } = await api.get<{ user: SessionUser }>('/auth/me');
      setUser(me);
    } catch (err) {
      // Un 401 acá es lo normal: significa «todavía no entró».
      if (!(err instanceof ApiError) || err.status !== 401) console.error(err);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void refresh(); }, [refresh]);

  const login = useCallback(async (email: string, password: string) => {
    const { user: me } = await api.post<{ user: SessionUser }>('/auth/login', { email, password });
    // Quien entra con una temporal va derecho a `CambiarContrasena`, que
    // necesita la actual para el endpoint de cambio. Guardarla acá evita que
    // la tenga que volver a escribir: dura lo que dura la pestaña —se borra
    // sola al cerrarla, a diferencia de `localStorage`— y sobrevive un F5, a
    // diferencia del estado de un componente. Si por lo que sea no aplica,
    // se limpia cualquier resto de un login anterior.
    try {
      if (me.must_change_password) sessionStorage.setItem(TEMP_PASSWORD_KEY, password);
      else sessionStorage.removeItem(TEMP_PASSWORD_KEY);
    } catch {
      // Modo privado o storage bloqueado: `CambiarContrasena` cae al campo
      // manual, que es el comportamiento de siempre.
    }
    setUser(me);
  }, []);

  const logout = useCallback(async () => {
    await api.post('/auth/logout');
    setUser(null);
    // Recarga completa: así no queda ni un dato del usuario anterior en la
    // caché de consultas ni en el estado de ningún componente.
    window.location.href = '/';
  }, []);

  const value = useMemo(
    () => ({ user, loading, login, logout, refresh }),
    [user, loading, login, logout, refresh],
  );

  /* Quien entró con una contraseña temporal no ve la aplicación: ve UNA
     pantalla, la de personalizarla. El guard vive acá —donde nace la sesión—
     y no en el router, así que no hay ruta ni marcador que lo esquive. Es
     cortesía de todos modos: el Worker responde 403 en toda la API mientras
     el flag esté puesto, y esta pantalla solo hace obvio el único camino. */
  return (
    <SessionContext value={value}>
      {user?.must_change_password ? <CambiarContrasena /> : children}
    </SessionContext>
  );
}

export function useSession(): SessionValue {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error('useSession fuera de SessionProvider');
  return ctx;
}

/** El usuario, dando por hecho que hay sesión. Solo dentro de rutas protegidas. */
export function useUser(): SessionUser {
  const { user } = useSession();
  if (!user) throw new Error('useUser sin sesión activa');
  return user;
}

/** A dónde mandar a cada quien después de entrar. */
export function homeFor(role: AppRole): string {
  switch (role) {
    case 'ADMIN': return '/admin';
    case 'EMPLOYEE': return '/empleado';
    default: return '/portal';
  }
}
